import { useEffect, useState, type FormEvent, type ReactElement } from "react";
import { Link, useParams } from "react-router-dom";

import { api, ApiError } from "../api/client";
import type { JoinStudyInfo } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { AuthShell } from "../components/AuthShell";
import { PasswordField } from "../components/PasswordField";

// Study-ID signup — the only way into a substudy (2026-08-14): the study team
// assigns the participant a personal study ID; they register with it plus a
// password and give no email or name. The ID + password are their sign-in
// credentials. Also served as /join/<slug> — the slug resolves to the study's
// name so the page says whose study it is; the form is identical (the ID
// itself determines the study).
export function RegisterStudyId(): ReactElement {
  const { registerStudyId } = useAuth();
  const { slug } = useParams<{ slug: string }>();
  const [studyName, setStudyName] = useState<string | null>(null);
  const [unknownLink, setUnknownLink] = useState(false);
  const [studyId, setStudyId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api<JoinStudyInfo>("GET", `/auth/join/${slug}`, { auth: false })
      .then((s) => setStudyName(s.name))
      .catch(() => setUnknownLink(true));
  }, [slug]);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await registerStudyId(studyId.trim(), password);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Registration failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthShell title="Account created" subtitle="You're ready to sign in.">
        <p className="muted">
          Your account is ready. Sign in with your study ID{" "}
          <strong>{studyId.trim().toUpperCase()}</strong> and the password you just chose. Keep
          your password safe: with no email on file, only your study team can arrange a reset.
        </p>
        <p className="auth-alt">
          <Link to="/login">Continue to sign in</Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={studyName ? `Join ${studyName}` : "Sign up with a study ID"}
      subtitle="For participants whose study team gave them an ID. No email is needed."
    >
      {unknownLink ? (
        <p className="muted">
          We could not find that study link. You can still sign up with the study ID your
          study team gave you.
        </p>
      ) : null}
      <form onSubmit={onSubmit} className="auth-form">
        <label className="field">
          <span>Study ID</span>
          <input
            required
            maxLength={64}
            value={studyId}
            onChange={(e) => setStudyId(e.target.value)}
            placeholder="e.g. NA-0001"
          />
        </label>
        <small className="field-hint">
          Your study team gave you this personal ID. It is yours alone, and it becomes your
          username for sign-in.
        </small>
        <PasswordField label="Password" value={password} onChange={setPassword} />
        <small className="field-hint">
          Avoid common or breached passwords; passphrases work well.
        </small>
        {error ? <p className="form-error">{error}</p> : null}
        <button type="submit" className="btn-primary-glow btn-block" disabled={busy}>
          {busy ? "Creating account…" : "Create account"}
        </button>
      </form>
      <p className="auth-alt">
        Signing up with an email instead? <Link to="/register">Standard sign-up</Link>
      </p>
      <p className="auth-alt">
        Already have an account? <Link to="/login">Sign in</Link>
      </p>
    </AuthShell>
  );
}
